import { RestClient } from "./restClient";

/** Whether this company's agent may send email, and where that came from. */
export interface EmailSetting {
  /** Off unless somebody turned it on. There is no platform default today, so
   *  in practice this is false until a company admin opts in. */
  enabled: boolean;
  /** "company" when this company chose, "platform" when inheriting, "default"
   *  when nothing is set anywhere. Computed by the SERVER — comparing values
   *  client-side would mislabel a company that deliberately chose the same
   *  value as the platform default, and that company IS overridden. */
  source: "company" | "platform" | "default";
  /** Whether THIS caller may change it. Company admins and platform admins
   *  may; the panel uses this to disable and explain the switch rather than
   *  rendering a live-looking control that 403s on click.
   *
   *  A UI affordance only — the API re-checks on every write, so a client that
   *  ignores this achieves nothing. */
  editable: boolean;
}

/** `null` CLEARS the row so the company inherits again; omitting `enabled`
 *  leaves it untouched. Those are three different requests, and an explicit
 *  `false` is a DECISION that outranks a platform default rather than an
 *  absence of one. */
export interface EmailSettingUpdate {
  enabled?: boolean | null;
}

/** Session-scoped cache, module scope: the panel unmounts on every tab switch
 *  in Organization settings, so a plain useEffect fetch would refire per
 *  click. Same reason as budgetSettingsApi's cache. */
let cached: EmailSetting | null = null;

export function clearEmailSettingCache(): void {
  cached = null;
}

/** Wraps /v1/settings/email. Methods throw RestError on non-2xx — notably 403
 *  when a non-admin attempts the write, which the panel renders rather than
 *  swallowing. */
export class EmailSettingsApi {
  constructor(private client: RestClient) {}

  async load(): Promise<EmailSetting> {
    if (cached) return cached;
    cached = await this.client.get<EmailSetting>("/v1/settings/email");
    return cached;
  }

  /** Opt this company in or out. Company admin. */
  async save(patch: EmailSettingUpdate): Promise<EmailSetting> {
    const saved = await this.client.put<EmailSetting>("/v1/settings/email", patch);
    cached = saved;
    return saved;
  }
}
